import type { EnnismoreTheme } from '@/ui/theme';

const fonts = {
  headline: 'portrait, serif',
  intro: 'portrait, sans-serif',
  subheader: 'portrait, sans-serif',
  body: '"aktiv-grotesk", sans-serif',
};

export const morgansOriginalsTheme: EnnismoreTheme = {
  themeKey: 'morgansoriginals',
  logoUrl: '/brands/morgansoriginals/images/morgans-logo-stacked.svg',
  faviconUrl: '/brands/morgansoriginals/images/favicon.svg',
  coBrandedDisloyaltyLogoUrl: '/disloyalty/brands/morgans.svg',
  space: ['0px', '8px', '16px', '20px', '24px', '32px', '40px', '56px'],
  fonts,
  text: {
    heading1: {
      fontFamily: fonts.headline,
      fontWeight: 400,
      fontSize: '32px',
      lineHeight: '56px',
      textTransform: 'uppercase',
    },
    heading2: {
      fontFamily: fonts.subheader,
      fontWeight: 400,
      fontSize: '56px',
      lineHeight: '64px',
      textTransform: 'uppercase',
    },
    heading3: {
      fontFamily: fonts.subheader,
      fontSize: '40px',
      lineHeight: '48px',
      textTransform: 'uppercase',
    },
    heading4: {
      fontFamily: fonts.subheader,
      fontSize: '32px',
      lineHeight: '40px',
      textTransform: 'uppercase',
    },
    heading5: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '32px',
      textTransform: 'uppercase',
    },
    heading6: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '24px',
      textTransform: 'uppercase',
    },
    intro1: {
      fontFamily: fonts.intro,
      fontSize: '40px',
      lineHeight: '56px',
    },
    intro2: {
      fontFamily: fonts.intro,
      fontSize: '24px',
      lineHeight: '40px',
    },
    subheader1: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '32px',
    },
    subheader2: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '32px',
    },
    subheader3: {
      fontFamily: fonts.subheader,
      fontSize: '14px',
      lineHeight: '24px',
    },
    bodyLarge: {
      fontFamily: fonts.body,
      fontSize: '20px',
      lineHeight: '32px',
    },
    bodyCopy: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '28px',
    },
    bodySmall: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '24px',
    },
    bodyExtraSmall: {
      fontFamily: fonts.body,
      fontSize: '11px',
      lineHeight: '22px',
    },
    captions: {
      fontFamily: fonts.body,
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
  colors: {
    white: 'white',
    textPrimary: 'black',
    textSecondary: '#2F2F2F',
    background: 'white',
    primary100: '#d9d9d9',
    primary200: 'white',
    primary300: '#2d2926',
    primary400: '#FFE7D9', // hox only
    primary500: '#2d2926',
    secondary100: '#000',
    secondary200: '#d9d9d9',
    secondary300: '#2d2926',
    secondary400: '#2d2926',
    error: '#DD3739',
    sale: '#B00020',
  },
  buttons: {
    primary: {
      backgroundColor: 'black',
      fontFamily: fonts.body,
      fontWeight: 500,
      fontSize: '16px',
      borderRadius: '26px',
      color: 'white',
      textTransform: 'uppercase',
      border: '1px solid black',
      transition: 'background-color 0.4s ease-in-out, color 0.4s ease-in-out',
      ':hover': {
        backgroundColor: 'transparent',
        color: 'black',
      },
      loadingIndicatorStyle: 'dark',
    },
    secondary: {
      backgroundColor: 'transparent',
      fontFamily: fonts.body,
      fontWeight: 500,
      fontSize: '16px',
      borderRadius: '26px',
      color: 'black',
      border: '1px solid black',
      textTransform: 'uppercase',
      transition: 'background-color 0.4s ease-in-out, color 0.4s ease-in-out',
      ':hover': {
        backgroundColor: 'black',
        color: 'white',
      },
      loadingIndicatorStyle: 'dark',
    },
    destructive: {
      backgroundColor: '#DD3739',
      fontFamily: fonts.body,
      fontWeight: 500,
      fontSize: '16px',
      borderRadius: '26px',
      color: 'white',
      // border: '1px solid white',
      textTransform: 'uppercase',
      mixBlendMode: 'difference',
      loadingIndicatorStyle: 'dark',
    },
  },
  keylines: {
    border: '1px solid rgba(0, 0, 0, 0.5)',
  },
  forms: {
    heading: {
      fontFamily: fonts.body,
    },
    label: {
      color: 'black',
    },
    input: {
      backgroundColor: 'white',
      color: 'black',
      fontFamily: fonts.body,
      fontSize: '16px',
    },
    radioButton: {
      color: 'black',
      selected: {
        backgroundColor: '#d9d9d980',
      },
    },
    radioGroup: {
      divider: { border: '1px solid #ccc' },
    },
  },
  fontFaceDefinitions: [
    {
      fontFamily: 'portrait',
      fontWeight: 400,
      sources: {
        woff2: '/brands/morgansoriginals/fonts/portrait-regular.woff2',
      },
    },
    {
      fontFamily: 'aktiv-grotesk',
      fontWeight: 300,
      sources: {
        woff2:
          'https://use.typekit.net/af/63d81f/000000000000000077359d5a/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n3&v=3',
      },
    },
    {
      fontFamily: 'aktiv-grotesk',
      fontWeight: 400,
      sources: {
        woff2:
          'https://use.typekit.net/af/ab3e12/000000000000000077359d4f/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3',
      },
    },
  ],
  componentProperties: {
    search: {
      results: {
        sidebarContainer: {
          rowGap: 'none',
          cancellationPolicy: {
            borderBottom: 'none',
            color: 'black',
            fontWeight: 300,
          },
          hotelPerks: {
            borderLeft: 'none',
            borderRight: 'none',
            mobile: {
              button: {
                textTransform: 'uppercase',
              },
            },
          },
        },
      },
      collapsibleForm: {
        searchSummary: {
          backgroundColor: 'white',
          border: '1px solid white',
          borderTop: '1px solid rgba(0, 0, 0, 0.5)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
          fontFamily: 'aktiv-grotesk, sans-serif',
          color: 'black',
          textTransform: 'none',
          modifySearchButton: {
            textTransform: 'uppercase',
          },
          expanded: {
            borderLeft: '1px solid white',
            borderRight: '1px solid white',
            borderBottom: 'none',
          },
          mobile: {
            padding: '28px 0',
            borderLeft: '1px solid white',
            borderRight: '1px solid white',
            marginTop: '24px',
          },
        },
        searchForm: {
          backgroundColor: 'white',
          color: 'black',
          border: '1px solid white',
          borderTop: 'none',
        },
      },
      errorCard: {
        mobile: {
          border: '1px solid white',
          margin: '0 28px',
        },
      },
    },
    calendar: {
      border: '1px solid black',
      header: {
        borderRadius: '3px 3px 0 0',
        borderBottom: '1px solid black',
        caption: {
          fontFamily: fonts.body,
          fontWeight: 500,
          color: 'black',
        },
        arrow: {
          stroke: 'none',
        },
      },
      body: {
        date: {
          selected: {
            backgroundColor: '#2350E9',
          },
        },
      },
    },
    paymentMethods: {
      googlePay: {
        style: 'light',
        borderRadius: '26px',
        border: '1px solid black',
      },
      applePay: {
        style: 'light',
        borderRadius: '26px',
      },
    },
    sidebarCards: {
      primary: {
        borderRadius: '0px',
        padding: 'none',
        borderColor: 'primary100',
        borderLeft: 'none',
        borderRight: 'none',
        paddingTop: '20px',
        paddingBottom: '20px',
      },
      secondary: {
        borderRadius: '0px',
        padding: 'none',
        borderColor: 'primary100',
        borderLeft: 'none',
        borderRight: 'none',
        paddingTop: '20px',
        paddingBottom: '20px',
      },
    },
    banners: {
      color: 'textSecondary',
    },
    checkoutSidebar: {
      rowGap: 'none',
      cancellationPolicy: {
        padding: '20px 24px',
        border: 'none',
        description: {
          color: 'black',
        },
      },
    },
    cards: {
      primary: {
        border: '1px solid rgba(0, 0, 0, 0.5)',
        backgroundColor: 'white',
        color: 'black',
        divider: {
          borderTop: '1px solid #ccc',
        },
      },
    },
    modals: {
      color: 'black',
      backgroundColor: 'white',
    },
    page: {
      background: 'white',
    },
    bookingManagement: {
      pageWrapper: {
        login: { alignContent: 'center' },
        verify: { alignContent: 'center' },
      },
      bookingCard: {
        button: {
          variant: 'secondary',
        },
        header: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
        },
        footer: {
          borderTop: '1px solid rgba(0, 0, 0, 0.5)',
        },
        backgroundColor: 'white',
        cancelled: {
          opacity: 1,
        },
        cancellationModal: {
          confirmButton: {
            variant: 'primary',
          },
        },
      },
    },
    confirmation: {
      rowGap: '0',
      headerImage: {
        height: '480px',
        objectFit: 'cover',
        container: {
          margin: '0',
          width: '100%',
          paddingBottom: '48px',
        },
      },
      bookingConfirmationAdditionalDetailsContainer: {
        backgroundColor: 'white',
        border: '1px solid white',
        borderLeft: 'none',
        borderRight: 'none',
        padding: '40px 0px',
        margin: '0px 28px',
        mobile: {
          padding: '28px 0',
          margin: '0 28px',
        },
      },
      anyQuestionsCard: {
        borderLeft: 'none',
        borderRight: 'none',
        padding: '40px 0px',
        margin: '0px 28px',
        mobile: {
          margin: '0 28px',
          padding: '28px 0',
        },
      },
      restaurantsCard: {
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        padding: '40px 0px',
        margin: '0px 28px',
        mobile: {
          margin: '0 28px',
          padding: '28px 0',
        },
      },
      whereToFindUsCard: {
        borderTop: 'none',
        borderLeft: 'none',
        borderBottom: 'none',
        borderRight: 'none',
        padding: '40px 0px',
        margin: '0px 28px',
        planYourJourneyLink: {
          color: 'white',
        },
        mobile: {
          margin: '0 28px',
          padding: '28px 0',
        },
      },
      bookingConfirmationDetailsCard: {
        bookingNumberHeading: {
          fontFamily: fonts.body,
        },
        manageMyBooking: {
          link: {
            color: 'black',
            fontFamily: fonts.body,
          },
        },
        termsAndConditions: {
          fontSize: '16px',
        },
        paymentCancellationPolicy: {
          description: {
            fontSize: '16px',
            color: 'black',
          },
        },
      },
    },
    sideDrawer: {
      backgroundColor: 'white',
      closeButton: {
        color: 'black',
      },
    },
    paymentPendingPage: {
      color: 'white',
    },
    transportOptions: {
      icon: {
        filter: 'brightness(0) invert(1) brightness(2) contrast(100)',
      },
    },
    hotelPerk: {
      icon: {
        filter: 'brightness(0) invert(1) brightness(2) contrast(100)',
      },
    },
    halfScreenLayout: {
      borderRight: 'none',
    },
    staySummary: {
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      backgroundColor: 'white',
      summarySection: {
        backgroundColor: 'white',
      },
      color: 'black',
      padding: '0',
      total: { value: { fontWeight: 500 } },
      title: {
        textTransform: 'uppercase',
        padding: '8px 0 8px',
      },
      dataRow: {
        label: {
          opacity: 1,
        },
      },
      dropdown: {
        opacity: 1,
        textTransform: 'uppercase',
        infoIcon: {
          filter: 'brightness(0) saturate(100%)',
        },
      },
    },
    bedroomCard: {
      backgroundColor: 'white',
      color: 'black',
      roomSpecItem: {
        color: '#757575',
        value: {
          color: 'black',
          fontSize: '16px',
        },
        icon: {
          filter: 'grayscale(100%) brightness(150%) contrast(90%)',
        },
      },
    },
    header: {
      logo: {
        borderRight: 'none',
        left: '80px',
        padding: '30px',
      },
      wizard: {
        step: {
          fontWeight: 300,
          active: {
            fontWeight: 500,
          },
          textTransform: 'uppercase',
        },
      },
    },
    footer: {
      color: 'black',
    },
  },
  icons: {},
  __temporary: {
    displayCarTransportOnNewLine: true,
  },
};
